// pkg192 — agentFullAccess (Hanzelik only; read from getMyPermissions and getFirmSettings): when the flag is strictly true, the Agent column uses the same toggles as Staff for manage_staff / mass_email / import_clio / purchase_billing. A missing or false flag keeps the padlocks. Owner-only controls stay locked — delivered 2026-10-31
// pkg189 — Agent role column (D-AG6): the AI-agent role joins Attorney/Accounting/Staff in the matrix; manage_staff / mass_email / import_clio / purchase_billing are locked off for it (the backend refuses them and adminAuth hard-denies the routes), shown as a padlock instead of a toggle — delivered 2026-10-31
// pkg72a — green→navy conversion: decorative dark-green surfaces (#1a3a2a/#2a5a4a — banner gradients, featured borders, buttons, toggles, tabs) become NAVY_DEEP #0F1838 → NAVY #1E2761; SEMANTIC greens kept (status/success badges, category chip triads, ✓ text); selected-module card converts as a pair (navy border + pale-navy #F0F2F8 tint) — delivered 2026-07-12
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// pkg68 — purchase_billing added to PERMISSION_LABELS (staff Subscribe/Buy Credits/wallet ledger; default off, D-68h) — delivered 2026-07-12
import React, { useState, useEffect } from 'react';
import { adminApi } from '../utils/adminApi';

const PERMISSION_LABELS = {
  view_clients:       { label: 'View Clients',            desc: 'See the client list and client detail pages' },
  edit_clients:       { label: 'Edit Clients',            desc: 'Edit client info, add dates, links, notes' },
  reset_passwords:    { label: 'Reset Client Passwords',  desc: 'Reset portal passwords for clients' },
  send_welcome_email: { label: 'Send Welcome Emails',     desc: 'Send welcome emails to clients' },
  view_billing:       { label: 'View Billing',            desc: 'See billing entries and hourly rates' },
  edit_billing:       { label: 'Edit Billing',            desc: 'Add and delete billing entries' },
  generate_discovery: { label: 'Generate Discovery',      desc: 'Generate interrogatories and RFP documents' },
  discovery_response: { label: 'Discovery Responses',     desc: 'Upload, answer, and generate responses to opposing counsel\'s discovery' },
  import_clio:        { label: 'Clio Import',             desc: 'Import matters, contacts, and activities from Clio' },
  mass_email:         { label: 'Mass Email',              desc: 'Send emails to multiple clients at once' },
  firm_dates:         { label: 'Firm-Wide Dates',         desc: 'Add and manage dates visible to all clients' },
  view_admin_data:    { label: 'View Client Submissions', desc: 'View what clients have saved and submitted' },
  manage_staff:       { label: 'Manage Staff',            desc: 'Add, edit, and remove staff members' },
  manage_modules:     { label: 'Manage Modules',          desc: 'Create and edit modules (portal sections) and case-type templates' },
  purchase_billing:   { label: 'Purchase Subscriptions & Credits', desc: 'Subscribe the firm and buy AI credit packs; see the Firm Wallet ledger. Off for all staff by default — owners can always purchase' },
};

const ROLES = ['attorney', 'accounting', 'staff', 'agent']; // pkg189

const ROLE_META = {
  attorney:   { label: 'Attorney',   color: '#2f5496', bg: '#f0f4ff' },
  accounting: { label: 'Accounting', color: '#5b3a8c', bg: '#f5f0fa' },
  staff:      { label: 'Staff',      color: '#555',    bg: '#f5f5f5' },
  agent:      { label: 'Agent',      color: '#534AB7', bg: '#EEEDFE' }, // pkg189 — AI-agent accounts
};

// pkg189 (D-AG5): keys the agent role cannot hold on a normal firm. Mirrors
// AGENT_FORBIDDEN_KEYS in routes/adminUsers.js; the matrix shows a lock
// instead of a toggle. pkg192: a strict true agentFullAccess (Hanzelik) uses
// the Staff toggle instead. Owner-only actions are not in this set.
const AGENT_LOCKED_KEYS = new Set(['manage_staff', 'mass_email', 'import_clio', 'purchase_billing']);
function isLocked(role, key, agentFullAccess) {
  return role === 'agent' && AGENT_LOCKED_KEYS.has(key) && agentFullAccess !== true;
}

function LockedCell() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }} title="Not available to agent accounts">
      <div style={{ width: 48, height: 26, borderRadius: 13, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: 13 }}>🔒</div>
      <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 10, color: '#aaa', fontWeight: 600 }}>Locked</div>
    </div>
  );
}

function Toggle({ enabled, saving, onToggle }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
      <button
        onClick={onToggle}
        disabled={saving}
        style={{
          width: 48, height: 26, borderRadius: 13,
          background: enabled ? '#0F1838' : '#ddd',
          border: 'none', cursor: saving ? 'default' : 'pointer',
          position: 'relative', transition: 'background 0.2s', flexShrink: 0,
        }}>
        <span style={{
          position: 'absolute', top: 3,
          left: enabled ? 25 : 3,
          width: 20, height: 20, borderRadius: '50%',
          background: '#fff', transition: 'left 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </button>
      <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 10, color: enabled ? '#0F1838' : '#aaa', fontWeight: 600 }}>
        {saving ? '...' : enabled ? 'Allowed' : 'Denied'}
      </div>
    </div>
  );
}

export default function AdminRolesPage() {
  const [permissions, setPermissions] = useState([]);
  const [agentFullAccess, setAgentFullAccess] = useState(false); // pkg192
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState('');
  const [error,       setError]       = useState('');

  useEffect(() => {
    let cancelled = false;
    // pkg192 — agentFullAccess rides the existing my-permissions and
    // firm-settings payloads. Either helper reporting strict true unlocks
    // the four Agent toggles; a missing field, false, or a failed read
    // (staff may 403 on firm settings) leaves the padlocks up. The matrix
    // waits so Hanzelik does not flash locks and then toggles.
    Promise.all([
      adminApi.getPermissions(),
      adminApi.getMyPermissions().catch(() => null),
      adminApi.getFirmSettings().catch(() => null),
    ])
      .then(([data, mine, firm]) => {
        if (cancelled) return;
        setPermissions(data);
        setAgentFullAccess(mine?.agentFullAccess === true || firm?.agentFullAccess === true);
        setLoading(false);
      })
      .catch(err => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  function getEnabled(role, key) {
    const p = permissions.find(p => p.role === role && p.permission_key === key);
    return p ? p.enabled : false;
  }

  async function toggle(role, key, currentValue) {
    setSaving(`${role}-${key}`); setError('');
    try {
      await adminApi.setPermission(role, key, !currentValue);
      // After save: update existing row OR insert a new one if no row existed.
      // The previous version only ran `.map(...)` which silently no-op'd when
      // there was no row to update — typical for permissions like `accounting`
      // where the schema only seeds a partial set of keys. The result was a
      // successful backend write followed by a stale UI until page reload.
      setPermissions(prev => {
        const existing = prev.find(p => p.role === role && p.permission_key === key);
        if (existing) {
          return prev.map(p =>
            p.role === role && p.permission_key === key
              ? { ...p, enabled: !currentValue }
              : p
          );
        }
        return [...prev, { role, permission_key: key, enabled: !currentValue }];
      });
    } catch (err) { setError(err.message); }
    finally { setSaving(''); }
  }

  const thStyle = { padding: '10px 14px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif',
    textTransform: 'uppercase', letterSpacing: '0.04em', color: '#fff', fontWeight: 600 };

  return (
    <div>
      <h1 className="page-title">Default Permissions</h1>
      <p className="page-subtitle">
        These are the default permissions applied when a new attorney or staff member is added.
        Per-user permissions can be customized individually from each profile in <strong>Staff Management</strong>.
        Owner permissions are fixed and cannot be changed.
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="alert alert-info" style={{ marginBottom: 20 }}>
        <strong>Owner</strong> — Always has full access to everything. Cannot be restricted.
      </div>

      {loading ? (
        <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888' }}>Loading...</div>
      ) : (
        <>
          {/* DESKTOP TABLE */}
          <div className="desktop-only">
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead>
                  <tr style={{ background: '#0F1838' }}>
                    <th style={{ ...thStyle, width: '36%' }}>Permission</th>
                    {ROLES.map(role => {
                      const meta = ROLE_META[role];
                      return (
                        <th key={role} style={{ ...thStyle, textAlign: 'center', background: meta.color, width: '16%' }}>
                          {meta.label}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(PERMISSION_LABELS).map(([key, { label, desc }], i) => (
                    <tr key={key} style={{ borderBottom: '1px solid #eee', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 500 }}>{label}</div>
                        <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#888', marginTop: 2 }}>{desc}</div>
                      </td>
                      {ROLES.map(role => {
                        const enabled = getEnabled(role, key);
                        return (
                          <td key={role} style={{ padding: '12px 14px', textAlign: 'center' }}>
                            {isLocked(role, key, agentFullAccess) ? <LockedCell /> : (
                            <Toggle
                              enabled={enabled}
                              saving={saving === `${role}-${key}`}
                              onToggle={() => toggle(role, key, enabled)}
                            />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* MOBILE CARDS — one card per permission with toggles per role */}
          <div className="mobile-only">
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {ROLES.map(role => {
                const meta = ROLE_META[role];
                return (
                  <div key={role} style={{ flex: 1, textAlign: 'center', background: meta.color, color: '#fff',
                                  padding: '6px 0', borderRadius: 4, fontSize: 12, fontFamily: 'Arial, sans-serif', fontWeight: 600 }}>
                    {meta.label}
                  </div>
                );
              })}
            </div>
            {Object.entries(PERMISSION_LABELS).map(([key, { label, desc }]) => (
              <div key={key} className="card" style={{ padding: '12px 14px', marginBottom: 8 }}>
                <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{label}</div>
                <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#888', marginBottom: 10 }}>{desc}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {ROLES.map(role => {
                    const meta = ROLE_META[role];
                    const enabled = getEnabled(role, key);
                    return (
                      <div key={role} style={{
                        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                        background: meta.bg,
                        padding: '8px 0', borderRadius: 4,
                      }}>
                        {isLocked(role, key, agentFullAccess) ? <LockedCell /> : (
                        <Toggle
                          enabled={enabled}
                          saving={saving === `${role}-${key}`}
                          onToggle={() => toggle(role, key, enabled)}
                        />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
